// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState, useEffect, useRef } from 'react';
import { CSVLink } from 'react-csv';
import { Typography, TableHead, Table, TableRow, TableBody, TableCell, Divider, CircularProgress, Snackbar, Alert } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { useParams } from 'react-router-dom';
import { InView } from 'react-intersection-observer';
import exportIcon from 'Assets/export.png';
import orderListIcon from 'Assets/order_list_icon.png';
import InfoIcon from '@mui/icons-material/Info';
import { GetProjectDetailsRoute } from 'Utils/routes';
import { URL } from 'Config';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LoadingSpinner from 'Components/LoadingSpinner';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { WideLightTooltip } from 'Components/Tooltip';
import SearchField from 'Components/SearchField';
import OrderListItem from './orderListItem';
import { GetOrderHistoryThunk, ExportOrderHistoryThunk } from 'Features/order/orderThunk';
import { ResetOrderHistoryAction } from 'Features/order/orderActions';
import { GetOrgTeamsThunk } from '../accountThunk';
import { GetOrgThunk } from 'Features/admin/adminThunk';
import { SetOrgIdAction } from 'Features/admin/adminActions';
import stylesDef from './styles';
import { buildOrderHistoryCsvRow, orderHistoryCsvHeaders } from 'Utils/orderHistoryCsv';

export const OrderHistory = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const list = useAppSelector((state) => state.order.historyList);
  const { historyTotalCount, historyPage, historyLoadAll, historyLoadingMore } = useAppSelector((state) => state.order);
  const organization = useAppSelector((state) => (_ADMIN_ ? state.admin?.org?.companyName : state.account.org?.companyName));
  const { orgId } = useParams();
  const { orgTeams } = useAppSelector((state) => state.account);
  const { salesNoDollars, salesNoCredits } = useFlags();

  // The column set and the rows live in Utils/orderHistoryCsv (AUT-6569), so the redaction rules are tested
  const headers = orderHistoryCsvHeaders({ salesNoCredits, salesNoDollars, admin: _ADMIN_ });
  const [showLoader, setShowLoader] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [fileName, setFileName] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [csvReady, setCsvReady] = useState(false);
  // @types/react-csv folds anchor HTML props into CSVLink's props, so its ref demands
  // Ref<CSVLink> & Ref<HTMLAnchorElement>; the intersection satisfies both. At runtime the
  // instance is the CSVLink component, which exposes the real anchor as `link`.
  const csvLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);

  // Orders arrive paged from the server with the project name/type already joined, so the page no
  // longer loads the org's projects at all (AUT-6351).
  useEffect(() => {
    if (_ADMIN_) dispatch(SetOrgIdAction(orgId));
    const loadData = async () => {
      setShowLoader(true);
      if (_ADMIN_) {
        await dispatch(GetOrgThunk(orgId));
      }
      await dispatch(GetOrgTeamsThunk());
      dispatch(ResetOrderHistoryAction(''));
      await dispatch(GetOrderHistoryThunk(orgId, 0));

      setShowLoader(false);
    };

    loadData();
  }, []);

  const prepareCsvData = (orders: IOrder[]) =>
    orders.map((order) => buildOrderHistoryCsvRow(order, {
      salesNoCredits,
      salesNoDollars,
      teams: orgTeams,
      projectLink: _ADMIN_ ? undefined : (ownerProject) => URL + GetProjectDetailsRoute(ownerProject),
    }));

  // Export covers the FULL filtered set via the server's export path (AUT-6351) — the paged
  // historyList holds only loaded pages, so building the CSV from it would silently truncate.
  const handleExportCsv = async () => {
    window?.pendo?.track('Export Order History');
    setExporting(true);
    setExportError(false);
    try {
      const result = await dispatch(ExportOrderHistoryThunk(orgId));
      if (!result.success) {
        throw new Error(result.message);
      }
      setCsvData(prepareCsvData(result.data));
      setFileName(`${organization}_orderhistory_${moment().format('YYYYMMDD')}.csv`);
      setCsvReady(true);
    } catch (error) {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (csvReady) {
      csvLinkRef.current?.link?.click();
      setCsvReady(false);
    }
  }, [csvReady]);

  // Server-side search across every page (AUT-6351): reset the paged state to the new term and
  // refetch page 0. SearchField debounces (500 ms); the thunk drops stale in-flight responses.
  const handleSearchChange = (e: string): void => {
    dispatch(ResetOrderHistoryAction(e));
    dispatch(GetOrderHistoryThunk(orgId, 0));
  };

  return (
    <LoadingSpinner loading={showLoader}>
      <Box sx={styles.pageWrapper}>
        <Box sx={styles.headerWrapper}>
          <Box sx={styles.itemsWrapper}>
            <SearchField
              sx={styles.icons}
              placeholder="Search"
              debounceTimeout={500}
              onChange={(e) => handleSearchChange(e)}
            />
            <Typography variant="h6" sx={styles.countText}>{`${historyTotalCount} orders`}</Typography>
            <Box sx={styles.exportButton} onClick={handleExportCsv}>
              {exporting ? <CircularProgress size={20} /> : <img src={exportIcon} alt="export .csv" />}
              <Typography variant="h6" sx={styles.exportButtonText}>Export CSV</Typography>
            </Box>
            <Box sx={styles.hiddenLink}>
              <CSVLink data={csvData} headers={headers} filename={fileName} ref={csvLinkRef} />
            </Box>
          </Box>
          <Divider sx={styles.divider} />
        </Box>
        <Snackbar open={exportError} autoHideDuration={6000} onClose={() => setExportError(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="error">Unable to export the order history. Please try again.</Alert>
        </Snackbar>
        <Table sx={styles.table} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableCell}>
                <Typography variant="h4">Order Name</Typography>
              </TableCell>
              <TableCell sx={styles.tableCell} align="right">
                <Typography variant="h4">Reference Id</Typography>
              </TableCell>
              <TableCell sx={styles.tableCell} align="right">
                <Typography variant="h4">Team</Typography>
              </TableCell>
              <TableCell sx={styles.tableCell} align="right">
                <Typography variant="h4">Date</Typography>
              </TableCell>
              <TableCell sx={styles.tableCell} align="right">
                <Typography variant="h4">Status</Typography>
              </TableCell>
              <TableCell sx={styles.tableCell} align="right">
                <Typography variant="h4">Order Acres</Typography>
              </TableCell>
              <TableCell sx={styles.tableCell} align="right">
                <Typography variant="h4">Project Mileage</Typography>
              </TableCell>
              {!salesNoCredits && (
                <TableCell sx={styles.tableCell} align="right">
                  <Typography variant="h4">Charged Credits</Typography>
                </TableCell>
              )}
              {!salesNoDollars && (
                <TableCell sx={styles.tableCell} align="right">
                  <Box sx={styles.costHeader}>
                    <Typography variant="h4">Cost</Typography>
                    <WideLightTooltip
                      placement="bottom"
                      interactive
                      title={(
                        <Box>
                          Projects submitted prior to July 2022 will continue to display the prices charged at the time of submission.
                          Orders from July 2022 onwards have their add-ons charged in credits.
                        </Box>
                      )}
                    >
                      <Box sx={styles.infoIcon}>
                        <InfoIcon sx={styles.icon} />
                      </Box>
                    </WideLightTooltip>
                  </Box>
                </TableCell>
              )}
              <TableCell sx={styles.iconTableCell} align="right" />
              {!_ADMIN_ && <TableCell sx={styles.tableCell} align="right" />}
            </TableRow>
          </TableHead>
          {list.length ? (
            <TableBody>
              {list.map((row: IOrder) => <OrderListItem order={row} key={row._id} />)}
            </TableBody>
          ) : null}
        </Table>
        {list.length > 0 && (
          <Box sx={styles.loadingMore}>
            {historyLoadingMore && <CircularProgress size={30} />}
            {!historyLoadingMore && (historyLoadAll ? (
              <Typography variant="h5">End of List</Typography>
            ) : (
              <>
                <InView onChange={(inView) => {
                  if (inView) {
                    dispatch(GetOrderHistoryThunk(orgId, historyPage + 1));
                  }
                }}
                />
                <Typography variant="h5">Loading more...</Typography>
              </>
            ))}
          </Box>
        )}
        {list.length === 0 && !historyLoadingMore ? (
          <Box sx={styles.noOrderContainer}>
            <img src={orderListIcon} alt="icon" />
            <Typography variant="h5">There are no orders to show.</Typography>
          </Box>
        ) : null}
      </Box>
    </LoadingSpinner>
  );
};

export default OrderHistory;
